import { TreeNode } from 'primeng/api/treenode';
import { Observable } from 'rxjs';
import { FolderFormInstanceTree } from '../../models/folder-form-instance-tree.model';
import { FolderFormInstanceTreeService } from '../../services/folder-form-instance-tree.service';
import { TreeConstants } from './folder-tree.component';

/*
* This class just packages up existing code that builds an array of TreeNodes
    It would be good to make use of it in
    - FolderTreeComponent
    - PermissionsComponent
*/
export class TreeBuilder {

    private tree: FolderFormInstanceTree = null;
    private rootFolderId: number;
    private mode: string = TreeConstants.REGULAR_MODE;
    private rawFolderFormInstanceTree: FolderFormInstanceTree;
    private treeData: TreeNode[];
    private disableNodeForPermission: string;
    private canCreateFolderInRoot: boolean;

    constructor(private folderFormInstanceTreeService: FolderFormInstanceTreeService, mode: string = TreeConstants.REGULAR_MODE) {
        this.mode = mode;
    }

    public build(siteId: number, includeForms: boolean = false, disableNodeForPermission: string = null): Promise<TreeNode[]> {
        this.disableNodeForPermission = disableNodeForPermission;
        return this.folderFormInstanceTreeService.getTree(siteId, includeForms).then(response => {
            this.tree = FolderFormInstanceTreeService.unpackTree(response);
            this.rawFolderFormInstanceTree = this.tree;
            this.treeData = this.rawTreeToTreeNodes(this.rawFolderFormInstanceTree);
            return this.treeData;
        });
    }

    public get RootFolderId(): number {
        return this.rootFolderId;
    }
    public get RootFolderIsSelectable(): boolean {
        return this.canCreateFolderInRoot;
    }

    public rootFolderType(writeable: boolean): string {
        return this.canCreateFolderInRoot && writeable ? 'Folder' : 'unselectable_Folder';
    }

    // Converts the tree data from the server to nested TreeNode objects used to
    // populate the PrimeNG tree
    private rawTreeToTreeNodes(tree: FolderFormInstanceTree, json: TreeNode[] = []): any {
        if (tree.parentFolderId == null) {
            this.rootFolderId = tree.id;
            this.canCreateFolderInRoot = tree.canCreateFolder;
        }
        if ((tree.childFolders !== null) && (tree.childFolders.length > 0)) {
            this.buildTreeNode(tree.childFolders, json);
        }
        if ((tree.childFormInstances !== null) && (tree.childFormInstances.length > 0)) {
            this.buildTreeNode(tree.childFormInstances, json);
        }
        return json;
    }



    private buildTreeNode(treeNodes: FolderFormInstanceTree[], json: TreeNode[]) {
        for (let treeNode of treeNodes) {
            let isSelectable = this.getIsSelectable(treeNode);

            let node = {
                label: `${treeNode.name}`,
                icon: this.getIcon(treeNode),
                selectable: isSelectable,
                //type: treeNode.type,
                data: {
                    id: treeNode.id,
                    name: treeNode.name,
                    parentId: treeNode.parentFolderId,
                    type: treeNode.type,
                    referencedFormInstanceId: treeNode.referencedFormInstanceId,
                    isReferenced: treeNode.isReferenced,
                    canView: treeNode.canView,
                    canEdit: treeNode.canEdit,
                    canDelete: treeNode.canDelete,
                    canGrant: treeNode.canGrant,
                    canBuildFolders: treeNode.canBuildFolders,
                    canBuildFormInstances: treeNode.canBuildFormInstances,
                    canBuildForms: treeNode.canBuildForms,
                    canBuildWorkflows: treeNode.canBuildWorkflows,
                    canCreateFolder: treeNode.canCreateFolder,
                    canCreateForm: treeNode.canCreateForm
                },
                children: [],
                childFolders: [],
                childFormInstances: [],
                key: treeNode.qualifiedId()
            };

            node['type'] = this.getNodeType(node, isSelectable); // see p-Template references in the html which style nodes based on type

            if (this.mode == TreeConstants.PERMISSIONS_MODE) {
                if (treeNode.canView) {
                    // This gets added to the searchable fields so user can search for viewable nodes
                    // (see filterBy attribute of <p-tree> in the template)
                    node.data['searchable'] = 'viewable,visible';
                }
            }
            json.push(node);
            this.rawTreeToTreeNodes(treeNode, node.children);
        }
    }
    private getIsSelectable(treeNode: FolderFormInstanceTree): boolean {
        if (this.disableNodeForPermission) {
            if (this.disableNodeForPermission == "CanCreateFolder" && !(treeNode.canCreateFolder || treeNode.canCreateForm)) {
                return false;
            } else if (this.disableNodeForPermission == "CanCreateForm" && !treeNode.canCreateForm) {
                return false;
            } else if (this.disableNodeForPermission == "CanDeleteForm" && !treeNode.canDeleteForm) {
                return false;
            } else {
                return true;
            }
        }
    }

    // helper methods
    private getNodeType(treeNode: TreeNode, isSelectable: boolean): string {
        let prefix = isSelectable ? "" : "unselectable_";

        if (this.mode == TreeConstants.PERMISSIONS_MODE) {
            return `${prefix}permissionsMode`;
        }
        else if (treeNode.data.referencedFormInstanceId) {
            return `${prefix}${TreeConstants.FORM_REF_NODE}`;
        } else if (treeNode.data.isReferenced) {
            return `${prefix}${TreeConstants.REFERENCED_FORM_NODE}`;
        }
        else {
            return `${prefix}${treeNode.data.type}`;
        }
    }

    public getIcon(folderFormInstance: FolderFormInstanceTree): string {
        let iconStyle = 'pi pi-folder';
        if (folderFormInstance.isReferenced) {
            iconStyle = 'article';
            if (folderFormInstance.isReferencedCount > 0) {
                iconStyle = 'filter_' + folderFormInstance.isReferencedCount.toString();
            }
            if (folderFormInstance.isReferencedCount > 9) {
                iconStyle = 'filter_9_plus';
            }
        }
        return iconStyle;
    }
}
